import { z } from 'zod';

import { ComponentValidator, ComponentPayloadValidator } from '../Component';

import { AbstractPort, Port } from '../fields/AbstractPort';

import { ComponentDefinition } from '../../types/ComponentDefinition';
import { PortPurpose } from '../fields';
import { environmental } from '../component-sections/environmental';

const port = AbstractPort.extend({
    AC: Port.AC.default({}),
    DC: Port.DC.default({}),
    description: z.string().default(''),
    purpose: PortPurpose.validator,
});

const PowerDistributionUnitBase = z.object({
    type: z.literal('powerDistributionUnit').default('powerDistributionUnit'),
    electrical: z
        .object({
            ports: z
                .array(port)
                .min(2)
                .default([
                    {
                        powerFlowDirection: 'input',
                    },
                    {
                        powerFlowDirection: 'output',
                    },
                ]),
        })
        .default({}),
    compatibleProducts: z.array(ComponentValidator.shape.id).default([]),
    environmental,
});

export const PowerDistributionUnitValidator = ComponentValidator.merge(PowerDistributionUnitBase);
export const PowerDistributionUnitPayloadValidator = ComponentPayloadValidator.merge(PowerDistributionUnitBase);

export const PowerDistributionUnitDefinition: ComponentDefinition = {
    type: 'powerDistributionUnit',
    name: 'Power Distribution Unit',
    plural: 'Power Distribution Units',
    description:
        'A power distribution unit (PDU) is a device fitted with multiple outputs designed to distribute electric power, especially to racks of computers and networking equipment located within a data center.',
    indicator: 'PDU',
    hasDynamicNumberOfPorts: true,
    interoperabilityDataModel: false,
    actsAsConverter: false,
    ports: {
        min: 2,
        max: Infinity,
        powerFlowDirections: ['input', 'output', 'bidirectional'],
    },
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: PowerDistributionUnitValidator,
        payload: PowerDistributionUnitPayloadValidator,
    },
};

export type PowerDistributionUnit = z.infer<typeof PowerDistributionUnitValidator>;
export type PowerDistributionUnitPayload = z.infer<typeof PowerDistributionUnitPayloadValidator>;
